/**
 * KIT product card — visual presentation.
 *
 * Iter-158.13.2.20f.16 — replaces the .20f.15 compact row with a
 * mobile-first visual card:
 *   • ~96px square image slot on the left with a white/light
 *     neutral background so approved product imagery (when we
 *     eventually gain it) reads correctly; neutral fallback icon
 *     when no compliant image is available.
 *   • Right column: 14–15px semibold display name wrapping up to
 *     3 lines (line-clamp-3 + break-words carried over from
 *     .20f.15), followed by the "View on Amazon ↗" action pill.
 *   • Image and fallback states share identical card geometry so
 *     the layout does not shift when a category later gains
 *     imagery on a per-product basis.
 *
 * MVP still treats every product as an equal recommendation: no
 * price, rating, review, Prime badge, availability, primary /
 * alternative hierarchy, or explanatory "opens in Amazon" copy.
 * Optional supervisor-authored `notes` render only when genuinely
 * present.
 *
 * "View on Amazon ↗" fires a fire-and-forget outbound-click event
 * to /api/kit/outbound BEFORE opening the external link. Analytics
 * failure never blocks the link.
 */
import { ExternalLink, Package } from "lucide-react";
import { logKitOutbound } from "../../lib/kitAnalytics";
import { resolveKitProductDisplayName, resolveKitProductImage } from "./productPresentation";

export function KitProductRow({ product, department, categorySlug }) {
    const handleOutboundClick = () => {
        logKitOutbound({
            department,
            category_slug: categorySlug,
            product_id: product.id,
            asin: product.asin,
        });
    };

    const displayName = resolveKitProductDisplayName(product);
    const image = resolveKitProductImage(product);

    return (
        <div
            data-testid={`kit-product-${product.id}`}
            className="rounded-2xl border border-white/5 bg-card/40 p-3 flex gap-3 items-stretch"
        >
            <div
                data-testid={`kit-product-${product.id}-thumb`}
                data-image-state={image ? "image" : "fallback"}
                className="h-24 w-24 shrink-0 rounded-xl bg-white/95 border border-white/10 flex items-center justify-center overflow-hidden"
            >
                {image ? (
                    <img
                        src={image.src}
                        alt={image.alt}
                        loading="lazy"
                        className="h-full w-full object-contain p-1.5"
                    />
                ) : (
                    <Package
                        size={28}
                        strokeWidth={1.5}
                        className="text-neutral-400"
                        aria-hidden="true"
                    />
                )}
            </div>
            <div className="min-w-0 flex-1 flex flex-col justify-between py-0.5">
                <div>
                    <div
                        data-testid={`kit-product-${product.id}-name`}
                        title={product.name}
                        className="text-[15px] font-semibold text-white leading-snug break-words line-clamp-3"
                    >
                        {displayName}
                    </div>
                    {product.notes ? (
                        <p
                            data-testid={`kit-product-${product.id}-notes`}
                            className="text-[11px] text-neutral-400 mt-1 leading-snug line-clamp-2"
                        >
                            {product.notes}
                        </p>
                    ) : null}
                </div>
                <a
                    data-testid={`kit-product-${product.id}-outbound`}
                    href={product.amazon_url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={handleOutboundClick}
                    className="tap mt-2 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold bg-primary text-primary-foreground hover:opacity-90 w-fit"
                >
                    View on Amazon
                    <ExternalLink size={12} />
                </a>
            </div>
        </div>
    );
}
